/**
 * Вид «Сезон» реестра хозяйств на каноне: факты по циклу, сортировка, ранг.
 *
 * Источник — `season/picture?summary=true` района (`by_farm` + `water_by_actor`).
 * Ход работ — по площади (решение №645): колонки в гектарах работ и «% площади»,
 * порядок по умолчанию — «не выполнено, га» (где ущерб). Место — справедливое:
 * по доле площади после срока внутри группы размера (аудит KPI 3.5); его
 * получают хозяйства с землёй ≥10 га и указанной площадью работ. Пустое
 * значение — «нет данных» / «площадь не указана» и при любой сортировке внизу.
 */

import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, Search } from 'lucide-react';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import { Icon } from '../ui/Icon';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonTable } from '../ui/Skeleton';
import { ha, m3Compact, num, pct } from '../lib/format';
import {
  MIN_AREA_HA,
  SIZE_GROUPS,
  areaGroupRanks,
  sizeGroupLabel,
  sortFarms,
  type SortDir,
  type SortKey,
} from '../data-source/api/farmBench';
import { noArea } from '../lib/areaWork';
import { useDistrictFarms } from './farmBenchParts';

const m = defineMessages({
  noData: { id: 'farm.worklist.noData', defaultMessage: 'нет данных', description: 'Вместо числа в таблице вида «Сезон», когда значение не названо; честное «нет данных», а не 0; строчными' },
  note: { id: 'farm.worklist.note', defaultMessage: 'Факты по агроциклу «{cycle}», ход работ — по площади: у каждой работы вес — площадь самой работы на поле (план — площадь размещения, карантин — земля контура, полив — доля посева), «га работ» — сумма площадей работ. «Не выполнено» — срок наступил, отчёта «выполнено» нет; «после срока» — окно закрылось, отчёта нет. «После срока, га земли» — земля, где есть такая работа, каждый гектар один раз (не больше земли хозяйства); «га работ» могут быть больше земли. Порядок по умолчанию — гектары работ невыполненного. «Место» — справедливое сравнение: по доле площади после срока (1 — самая большая) внутри своей группы размера ({groups} земли под агроциклом) и только у хозяйств с землёй ≥{min} га. «Работ на полях» — число работ, второй слой. «Вода» — подано от запрошенного. «Площадь не указана» / «нет данных» — сервер не назвал величину, это не ноль.', description: 'Пояснение над таблицей вида «Сезон» реестра хозяйств (хоким, менеджер); {cycle} — «Хлопок 2026», {groups} — группы размера через запятую, {min} — порог площади, число' },
  noteSynthetic: { id: 'farm.worklist.noteSynthetic', defaultMessage: 'Данные демонстрации — синтетика.', description: 'Добавляется в конец пояснения над таблицей, когда в срез включены демонстрационные данные' },
  searchPlaceholder: { id: 'farm.worklist.search.placeholder', defaultMessage: 'Поиск хозяйства по имени', description: 'Подсказка внутри поля поиска хозяйств (вид «Сезон»)' },
  searchAria: { id: 'farm.worklist.search.aria', defaultMessage: 'Поиск хозяйства по имени', description: 'aria-label поля поиска хозяйств (вид «Сезон»)' },
  hideSmall: { id: 'farm.worklist.hideSmall', defaultMessage: 'Скрыть малые (меньше {min} га земли или без площади работ)', description: 'Флажок над таблицей вида «Сезон»: скрыть малые хозяйства; {min} — порог площади, число' },
  hideSmallCount: { id: 'farm.worklist.hideSmallCount', defaultMessage: 'Скрыть малые (меньше {min} га земли или без площади работ, их {count})', description: 'Флажок над таблицей вида «Сезон» с числом скрытых; {min} — порог площади, {count} — сколько хозяйств скрыто, числа строкой' },
  loading: { id: 'farm.worklist.loading', defaultMessage: 'картину агроцикла района', description: 'Что грузится (читает диктор «Загружаю …»): картина агроцикла по району; подпись скелетона таблицы' },
  failTitle: { id: 'farm.worklist.fail.title', defaultMessage: 'Список не собран', description: 'Заголовок пустого состояния вида «Сезон», когда картина агроцикла недоступна' },
  failReason: { id: 'farm.worklist.fail.reason', defaultMessage: 'картина агроцикла не прочитана: {message}', description: 'Пояснение к «Список не собран» при ошибке; {message} — текст ошибки от сервера' },
  noCycle: { id: 'farm.worklist.fail.noCycle', defaultMessage: 'в срезе нет ни одного агроцикла', description: 'Пояснение к «Список не собран», когда в срезе нет агроциклов' },
  emptyTitle: { id: 'farm.worklist.empty.title', defaultMessage: 'Хозяйств не нашлось', description: 'Заголовок пустого состояния таблицы вида «Сезон»' },
  emptyQuery: { id: 'farm.worklist.empty.query', defaultMessage: 'по запросу «{query}» совпадений нет', description: 'Пояснение пустой таблицы при поиске; {query} — введённый запрос' },
  emptyNone: { id: 'farm.worklist.empty.none', defaultMessage: 'в агроцикле нет хозяйств с работами на полях', description: 'Пояснение пустой таблицы вида «Сезон» без поиска' },
  colPlace: { id: 'farm.worklist.col.place', defaultMessage: 'Место в группе', description: 'Колонка таблицы вида «Сезон»: место хозяйства среди хозяйств своей группы размера' },
  colFarm: { id: 'farm.worklist.col.farm', defaultMessage: 'Хозяйство', description: 'Колонка таблицы вида «Сезон»: хозяйство; нажатие сортирует' },
  colMassif: { id: 'farm.worklist.col.massif', defaultMessage: 'Массив', description: 'Колонка таблицы вида «Сезон»: массив; нажатие сортирует' },
  colLand: { id: 'farm.worklist.col.land', defaultMessage: 'Земля, га', description: 'Колонка таблицы вида «Сезон»: земля хозяйства в гектарах; узкая колонка' },
  colWorkHa: { id: 'farm.worklist.col.workHa', defaultMessage: 'Работы, га работ', description: 'Колонка таблицы вида «Сезон»: сумма площадей работ в гектарах работ (не земля); узкая колонка' },
  colDonePct: { id: 'farm.worklist.col.donePct', defaultMessage: 'Выполнено, % площади работ', description: 'Колонка таблицы вида «Сезон»: доля выполненного по площади работ; узкая колонка' },
  colUndone: { id: 'farm.worklist.col.undone', defaultMessage: 'Не выполнено, га работ', description: 'Колонка таблицы вида «Сезон»: гектары работ, срок которых наступил без отчёта «выполнено»; узкая колонка' },
  colOverdueHa: { id: 'farm.worklist.col.overdueHa', defaultMessage: 'После срока, га земли', description: 'Колонка таблицы вида «Сезон»: гектары земли с работой после срока (каждый гектар один раз); узкая колонка' },
  colWorks: { id: 'farm.worklist.col.works', defaultMessage: 'Работ на полях', description: 'Колонка таблицы вида «Сезон»: число работ на полях хозяйства; узкая колонка' },
  colConflicts: { id: 'farm.worklist.col.conflicts', defaultMessage: 'Расхождения', description: 'Колонка таблицы вида «Сезон»: расхождения отчёта и проверки; узкая колонка' },
  colWater: { id: 'farm.worklist.col.water', defaultMessage: 'Вода подана', description: 'Колонка таблицы вида «Сезон»: доля поданной воды от запрошенной; узкая колонка' },
  rankTitle: { id: 'farm.worklist.rank.title', defaultMessage: 'группа {group}', description: 'Всплывающая подсказка места хозяйства; {group} — название группы размера' },
  rankOf: { id: 'farm.worklist.rank.of', defaultMessage: 'из {of} · {group}', description: 'Мелкая строка под местом хозяйства; {of} — сколько хозяйств в группе, {group} — название группы размера' },
  smallNoRank: { id: 'farm.worklist.smallNoRank', defaultMessage: 'меньше {min} га — без места', description: 'Мелкая строка под именем малого хозяйства: места в группе не получает; {min} — порог площади, число' },
  overdueSub: { id: 'farm.worklist.overdueSub', defaultMessage: '{pct} площади работ', description: 'Мелкая строка в ячейке «После срока, га земли»; {pct} — процент строкой' },
  overdueSubWork: { id: 'farm.worklist.overdueSubWork', defaultMessage: '{ha} га работ · {pct} площади работ', description: 'Мелкая строка в ячейке «После срока, га земли»; {ha} — гектары работ после срока строкой, {pct} — процент строкой' },
  worksTitle: { id: 'farm.worklist.worksTitle', defaultMessage: 'после срока {overdue} из {total}', description: 'Всплывающая подсказка числа работ хозяйства; {overdue}/{total} — числа строкой' },
  waterFileTitle: { id: 'farm.worklist.waterFileTitle', defaultMessage: 'подано по файлу ирригации; заявок у событий нет, доля не считается', description: 'Всплывающая подсказка объёма воды в колонке «Вода подана», когда вода — события файла ирригации' },
  waterTitle: { id: 'farm.worklist.waterTitle', defaultMessage: 'запрошено {volume}', description: 'Всплывающая подсказка доли поданной воды; {volume} — запрошенный объём с единицей' },
  collapse: { id: 'farm.worklist.collapse', defaultMessage: 'Свернуть', description: 'Кнопка под таблицей вида «Сезон»: свернуть список до первых строк' },
  showAll: { id: 'farm.worklist.showAll', defaultMessage: 'Показать все {count}', description: 'Кнопка под таблицей вида «Сезон»: показать все строки; {count} — число строкой' },
});

const PAGE = 60;

const SORT_KEYS: SortKey[] = [
  'name', 'massif', 'areaHa', 'workHa', 'doneAreaPct', 'undoneHa', 'overdueHa', 'total', 'conflicts', 'waterShare',
];

/** По тексту — от А к Я, по числам — сначала большие; «выполнено» — сначала меньшие (отстающие). */
const FIRST_DIR: Record<SortKey, SortDir> = {
  name: 'asc', massif: 'asc', areaHa: 'desc', total: 'desc', reportedShare: 'desc',
  overdueShare: 'desc', conflicts: 'desc', waterShare: 'desc',
  workHa: 'desc', doneAreaPct: 'asc', undoneHa: 'desc', overdueHa: 'desc',
};

/** Старые адреса (`?sort=overdue_share` с Обзора, `overdueShare`, `reportedShare`) — на площадные колонки. */
const LEGACY_SORT: Record<string, SortKey> = { overdue_share: 'overdueHa', overdueShare: 'overdueHa', reportedShare: 'doneAreaPct' };

const haCell = (v: number | null) => (v === null ? noArea() : num(Math.round(v)));
const pct1 = (v: number | null) => (v === null ? noArea() : pct(v, 1));

export function FarmsWorklist() {
  const t = useT();
  const noData = t(m.noData);
  const district = useDistrictFarms();
  const [search, setSearch] = useSearchParams();
  const sortParam = search.get('sort');
  // `?sort=overdue_share` — прежняя ссылка «Все хозяйства» с обзора района.
  const effectiveKey: SortKey = (sortParam && LEGACY_SORT[sortParam]) || SORT_KEYS.find((k) => k === sortParam) || 'undoneHa';
  const dir: SortDir = search.get('dir') === 'asc' ? 'asc' : search.get('dir') === 'desc' ? 'desc' : FIRST_DIR[effectiveKey];
  const [query, setQuery] = useState('');
  const [hideSmall, setHideSmall] = useState(true);
  const [all, setAll] = useState(false);

  const ranks = useMemo(() => areaGroupRanks(district.metrics), [district.metrics]);
  const rankable = district.metrics.filter((m) => m.areaRankable);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = district.metrics.filter(
      (m) => (!hideSmall || m.areaRankable) && (!q || m.name.toLowerCase().includes(q)),
    );
    return sortFarms(pool, effectiveKey, dir);
  }, [district.metrics, hideSmall, query, effectiveKey, dir]);

  const sortBy = (k: SortKey) => {
    const next = new URLSearchParams(search);
    next.set('sort', k);
    next.set('dir', k === effectiveKey ? (dir === 'asc' ? 'desc' : 'asc') : FIRST_DIR[k]);
    setSearch(next, { replace: true });
  };

  const head = (k: SortKey, label: string, numeric = false) => (
    <th scope="col" className={numeric ? 'is-num' : undefined} aria-sort={k === effectiveKey ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="fb-sort" onClick={() => sortBy(k)}>
        {label}
        {k === effectiveKey ? <Icon icon={dir === 'asc' ? ArrowUp : ArrowDown} size={16} /> : null}
      </button>
    </th>
  );

  if (district.loading) {
    return <SkeletonTable label={t(m.loading)} rows={8} cols={10} />;
  }
  if (district.error || !district.cycle) {
    return (
      <EmptyState compact title={t(m.failTitle)} reason={district.error ? t(m.failReason, { message: district.error.message }) : t(m.noCycle)} />
    );
  }

  const shown = all ? rows : rows.slice(0, PAGE);
  const small = district.metrics.length - rankable.length;

  return (
    <>
      <p className="fb-note">
        {t(m.note, { cycle: district.cycleName ?? '', groups: SIZE_GROUPS.map((g) => g.label).join(', '), min: MIN_AREA_HA })}
        {district.synthetic ? ` ${t(m.noteSynthetic)}` : ''}
      </p>
        <div className="fb-toolbar">
          <label className="searchbox"><span className="searchbox__field"><Icon icon={Search} size={16} /><input type="search" placeholder={t(m.searchPlaceholder)} aria-label={t(m.searchAria)} value={query} onChange={(e) => setQuery(e.target.value)} /></span></label>
          <label>
            <input type="checkbox" checked={hideSmall} onChange={(e) => setHideSmall(e.target.checked)} />
            {small ? t(m.hideSmallCount, { min: MIN_AREA_HA, count: num(small) }) : t(m.hideSmall, { min: MIN_AREA_HA })}
          </label>
        </div>

        {rows.length === 0 ? (
          <EmptyState compact title={t(m.emptyTitle)} reason={query ? t(m.emptyQuery, { query }) : t(m.emptyNone)} />
        ) : (
          <div className="table-wrap">
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t(m.colPlace)}</th>
                    {head('name', t(m.colFarm))}
                    {head('massif', t(m.colMassif))}
                    {head('areaHa', t(m.colLand), true)}
                    {head('workHa', t(m.colWorkHa), true)}
                    {head('doneAreaPct', t(m.colDonePct), true)}
                    {head('undoneHa', t(m.colUndone), true)}
                    {head('overdueHa', t(m.colOverdueHa), true)}
                    {head('total', t(m.colWorks), true)}
                    {head('conflicts', t(m.colConflicts), true)}
                    {head('waterShare', t(m.colWater), true)}
                  </tr>
                </thead>
                <tbody>
                  {shown.map((f) => (
                    <tr key={f.id}>
                      <td className="fb-rank" title={ranks.has(f.id) ? t(m.rankTitle, { group: sizeGroupLabel(ranks.get(f.id)!.group) }) : undefined}>
                        {ranks.has(f.id) ? <>{ranks.get(f.id)!.place}<div className="fb-small t-caption">{t(m.rankOf, { of: num(ranks.get(f.id)!.of), group: sizeGroupLabel(ranks.get(f.id)!.group) })}</div></> : '—'}
                      </td>
                      <td>
                        <Link to={`/farms/${f.id}`}>{f.name}</Link>
                        {!f.areaRankable && <div className="fb-small t-caption">{f.workHa === null ? noArea() : t(m.smallNoRank, { min: MIN_AREA_HA })}</div>}
                      </td>
                      <td>{f.massifName ?? noData}</td>
                      <td className="is-num">{ha(f.areaHa)}</td>
                      <td className="is-num">{haCell(f.workHa)}</td>
                      <td className="is-num">{pct1(f.doneAreaPct)}</td>
                      <td className="is-num">{haCell(f.undoneHa)}</td>
                      <td className="is-num">
                        {f.overdueHa === null ? noArea() : <>{num(Math.round(f.overdueHa))}<div className="fb-small t-caption">{f.overdueWorkHa === null ? t(m.overdueSub, { pct: pct(f.overdueAreaPct, 1) }) : t(m.overdueSubWork, { ha: num(Math.round(f.overdueWorkHa)), pct: pct(f.overdueAreaPct, 1) })}</div></>}
                      </td>
                      <td className="is-num" title={f.overdue === null ? undefined : t(m.worksTitle, { overdue: num(f.overdue), total: num(f.total) })}>{num(f.total)}</td>
                      <td className="is-num">{f.conflicts === null ? noData : num(f.conflicts)}</td>
                      <td className="is-num" title={f.requestedM3 !== null ? t(m.waterTitle, { volume: m3Compact(f.requestedM3) }) : f.deliveredM3 !== null ? t(m.waterFileTitle) : undefined}>
                        {f.waterShare !== null ? pct(f.waterShare) : f.deliveredM3 !== null ? m3Compact(f.deliveredM3) : noData}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {rows.length > PAGE && (
              <button type="button" className="astra-button" onClick={() => setAll((v) => !v)}>
                {all ? t(m.collapse) : t(m.showAll, { count: num(rows.length) })}
              </button>
            )}
          </div>
        )}
    </>
  );
}
